package com.checkin.host.theme

import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.browser.localStorage
import org.jetbrains.compose.web.css.*

enum class ThemeMode(val storageKey: String, val dataTheme: String) {
    LIGHT("light", "light"),
    OBSIDIAN("obsidian", "obsidian"),
    ;

    companion object {
        const val STORAGE_KEY = "checkin_host_theme"

        fun fromStorage(): ThemeMode {
            val stored = localStorage.getItem(STORAGE_KEY) ?: return LIGHT
            return entries.firstOrNull { it.storageKey == stored } ?: LIGHT
        }
    }
}

data class ColorPalette(
    val bg: CSSColorValue,
    val surface: CSSColorValue,
    val surfaceHigh: CSSColorValue,
    val sidebar: CSSColorValue,
    val border: CSSColorValue,
    val borderLight: CSSColorValue,
    val primary: CSSColorValue,
    val primaryDark: CSSColorValue,
    val primaryGlow: String,
    val text: CSSColorValue,
    val textSub: CSSColorValue,
    val muted: CSSColorValue,
    val success: CSSColorValue,
    val successBg: String,
    val successBorder: String,
    val warning: CSSColorValue,
    val warningBg: String,
    val warningBorder: String,
    val review: CSSColorValue,
    val reviewBg: String,
    val reviewBorder: String,
    val hostReview: CSSColorValue,
    val hostReviewBg: String,
    val info: CSSColorValue,
    val infoBg: String,
    val danger: CSSColorValue,
    val dangerBg: String,
    val dangerBorder: String,
    val card: CSSColorValue,
    val navActive: CSSColorValue,
    val navActiveBg: String,
    val navActiveBorder: String,
    val overlayScrim: String,
    val cardShadow: String,
    val planCardGradient: String,
    val calendarSelectedBg: String,
    val calendarActivityBg: CSSColorValue,
)

private val lightPalette = ColorPalette(
    bg = Color("#F4F5F7"),
    surface = Color("#FFFFFF"),
    surfaceHigh = Color("#F9FAFB"),
    sidebar = Color("#FFFFFF"),
    border = Color("#E5E7EB"),
    borderLight = Color("#F3F4F6"),
    primary = Color("#6366F1"),
    primaryDark = Color("#4F52D4"),
    primaryGlow = "rgba(99,102,241,0.12)",
    text = Color("#111827"),
    textSub = Color("#374151"),
    muted = Color("#6B7280"),
    success = Color("#059669"),
    successBg = "rgba(5,150,105,0.1)",
    successBorder = "rgba(5,150,105,0.25)",
    warning = Color("#D97706"),
    warningBg = "rgba(217,119,6,0.1)",
    warningBorder = "rgba(217,119,6,0.3)",
    review = Color("#A16207"),
    reviewBg = "rgba(251,191,36,0.22)",
    reviewBorder = "rgba(251,191,36,0.45)",
    hostReview = Color("#4F46E5"),
    hostReviewBg = "rgba(99,102,241,0.1)",
    info = Color("#2563EB"),
    infoBg = "rgba(37,99,235,0.08)",
    danger = Color("#DC2626"),
    dangerBg = "rgba(220,38,38,0.08)",
    dangerBorder = "rgba(220,38,38,0.25)",
    card = Color("#FFFFFF"),
    navActive = Color("#4F46E5"),
    navActiveBg = "rgba(99,102,241,0.1)",
    navActiveBorder = "rgba(99,102,241,0.2)",
    overlayScrim = "rgba(15,23,42,0.45)",
    cardShadow = "0 1px 2px rgba(15, 23, 42, 0.04)",
    planCardGradient = "linear-gradient(135deg, #FFFFFF 0%, #F9FAFB 100%)",
    calendarSelectedBg = "rgba(99,102,241,0.12)",
    calendarActivityBg = Color("#F9FAFB"),
)

private val obsidianPalette = ColorPalette(
    bg = Color("#0D0F14"),
    surface = Color("#161A22"),
    surfaceHigh = Color("#1E2330"),
    sidebar = Color("#0A0C10"),
    border = Color("#2A2F3D"),
    borderLight = Color("#1E2330"),
    primary = Color("#6366F1"),
    primaryDark = Color("#4F52D4"),
    primaryGlow = "rgba(99,102,241,0.18)",
    text = Color("#F8FAFC"),
    textSub = Color("#CBD5E1"),
    muted = Color("#64748B"),
    success = Color("#10B981"),
    successBg = "rgba(16,185,129,0.12)",
    successBorder = "rgba(16,185,129,0.25)",
    warning = Color("#F59E0B"),
    warningBg = "rgba(245,158,11,0.12)",
    warningBorder = "rgba(245,158,11,0.35)",
    review = Color("#FCD34D"),
    reviewBg = "rgba(251,191,36,0.14)",
    reviewBorder = "rgba(251,191,36,0.3)",
    hostReview = Color("#A5B4FC"),
    hostReviewBg = "rgba(99,102,241,0.15)",
    info = Color("#38BDF8"),
    infoBg = "rgba(56,189,248,0.12)",
    danger = Color("#F87171"),
    dangerBg = "rgba(248,113,113,0.12)",
    dangerBorder = "rgba(248,113,113,0.25)",
    card = Color("#161A22"),
    navActive = Color("#A5B4FC"),
    navActiveBg = "rgba(99,102,241,0.15)",
    navActiveBorder = "rgba(99,102,241,0.25)",
    overlayScrim = "rgba(0,0,0,0.65)",
    cardShadow = "0 1px 3px rgba(0,0,0,0.4)",
    planCardGradient = "linear-gradient(135deg, #161A22 0%, #1a1f2e 100%)",
    calendarSelectedBg = "rgba(99,102,241,0.18)",
    calendarActivityBg = Color("#1E2330"),
)

object AppThemeHolder {
    val modeState = mutableStateOf(ThemeMode.fromStorage())

    var mode: ThemeMode
        get() = modeState.value
        set(value) {
            modeState.value = value
            persist(value)
            applyDocumentTheme(value)
        }

    val colors: ColorPalette
        get() = when (mode) {
            ThemeMode.LIGHT -> lightPalette
            ThemeMode.OBSIDIAN -> obsidianPalette
        }

    fun toggle() {
        mode = if (mode == ThemeMode.LIGHT) ThemeMode.OBSIDIAN else ThemeMode.LIGHT
    }

    fun persist(mode: ThemeMode) {
        localStorage.setItem(ThemeMode.STORAGE_KEY, mode.storageKey)
    }

    fun applyDocumentTheme(mode: ThemeMode) {
        kotlinx.browser.document.documentElement?.setAttribute("data-theme", mode.dataTheme)
    }

    fun init() {
        applyDocumentTheme(mode)
    }
}

/** Palette active — se met à jour quand le thème change (si un parent lit [AppThemeHolder.modeState]). */
object AppColors {
    val bg get() = AppThemeHolder.colors.bg
    val surface get() = AppThemeHolder.colors.surface
    val surfaceHigh get() = AppThemeHolder.colors.surfaceHigh
    val sidebar get() = AppThemeHolder.colors.sidebar
    val border get() = AppThemeHolder.colors.border
    val borderLight get() = AppThemeHolder.colors.borderLight
    val primary get() = AppThemeHolder.colors.primary
    val primaryDark get() = AppThemeHolder.colors.primaryDark
    val primaryGlow get() = AppThemeHolder.colors.primaryGlow
    val text get() = AppThemeHolder.colors.text
    val textSub get() = AppThemeHolder.colors.textSub
    val muted get() = AppThemeHolder.colors.muted
    val success get() = AppThemeHolder.colors.success
    val successBg get() = AppThemeHolder.colors.successBg
    val successBorder get() = AppThemeHolder.colors.successBorder
    val warning get() = AppThemeHolder.colors.warning
    val warningBg get() = AppThemeHolder.colors.warningBg
    val warningBorder get() = AppThemeHolder.colors.warningBorder
    val review get() = AppThemeHolder.colors.review
    val reviewBg get() = AppThemeHolder.colors.reviewBg
    val reviewBorder get() = AppThemeHolder.colors.reviewBorder
    val hostReview get() = AppThemeHolder.colors.hostReview
    val hostReviewBg get() = AppThemeHolder.colors.hostReviewBg
    val info get() = AppThemeHolder.colors.info
    val infoBg get() = AppThemeHolder.colors.infoBg
    val danger get() = AppThemeHolder.colors.danger
    val dangerBg get() = AppThemeHolder.colors.dangerBg
    val dangerBorder get() = AppThemeHolder.colors.dangerBorder
    val card get() = AppThemeHolder.colors.card
    val navActive get() = AppThemeHolder.colors.navActive
    val navActiveBg get() = AppThemeHolder.colors.navActiveBg
    val navActiveBorder get() = AppThemeHolder.colors.navActiveBorder
    val overlayScrim get() = AppThemeHolder.colors.overlayScrim
    val cardShadow get() = AppThemeHolder.colors.cardShadow
    val planCardGradient get() = AppThemeHolder.colors.planCardGradient
    val calendarSelectedBg get() = AppThemeHolder.colors.calendarSelectedBg
    val calendarActivityBg get() = AppThemeHolder.colors.calendarActivityBg
}

fun StyleScope.cardStyle() {
    backgroundColor(AppColors.surface)
    borderRadius(12.px)
    padding(20.px)
    border(1.px, LineStyle.Solid, AppColors.border)
    property("box-shadow", AppColors.cardShadow)
}

fun StyleScope.primaryButton() {
    backgroundColor(AppColors.primary)
    color(Color("#FFFFFF"))
    borderRadius(8.px)
    padding(10.px, 18.px)
    border(0.px)
    cursor("pointer")
    fontWeight("600")
    property("font-size", "14px")
    property("transition", "background 0.15s ease, box-shadow 0.15s ease")
}

fun StyleScope.secondaryButton() {
    backgroundColor(AppColors.surfaceHigh)
    color(AppColors.textSub)
    borderRadius(8.px)
    padding(10.px, 18.px)
    border(1.px, LineStyle.Solid, AppColors.border)
    cursor("pointer")
    fontWeight("500")
    property("font-size", "14px")
    property("transition", "background 0.15s ease")
}

fun StyleScope.inputStyle() {
    width(100.percent)
    padding(10.px, 12.px)
    borderRadius(8.px)
    border(1.px, LineStyle.Solid, AppColors.border)
    property("font-size", "15px")
    property("box-sizing", "border-box")
    backgroundColor(AppColors.surfaceHigh)
    color(AppColors.text)
    property("outline", "none")
    property("transition", "border-color 0.15s ease")
}
